/**
 * Session-Level Dataset Builder
 * One row per student — serves the convergence story: bigrams, strategies, and taxonomy.
 */

import { getMessagesBetweenUserAndAI, AI_AGENT_ID } from '../../workspace/service/workspaceMessageService'

// ─── Helpers ─────────────────────────────────────────────────

function esc(v) { return `"${String(v ?? '').replace(/"/g, '""')}"` }

function classifyProfile(edits, runs, errors, hasPriorAi) {
  if (edits === 0 && runs === 0) return hasPriorAi ? 'Passive' : 'Cold Start'
  if (runs === 0 || (runs >= 1 && errors === 0)) return 'Moderate'
  if (runs >= 3 && errors >= 3) return 'Intensive'
  return 'Active'
}

// ─── Segment / Sequence Parsing ──────────────────────────────

function parseSegments(analytics) {
  const segs = (analytics?.autoSegments || []).filter(s => !s.isSystemEvent && s.behavior)
  return segs.map(s => ({
    id: s.behavior?.id || 'unknown',
    label: s.behavior?.label || 'Unknown',
    duration: (s.endTime - s.startTime) / 1000,
    startTime: s.startTime,
    endTime: s.endTime,
  }))
}

function buildSequenceString(segments) {
  return segments.map(s => {
    const dur = s.duration
    const timeStr = dur >= 60 ? `${Math.floor(dur / 60)}m${Math.round(dur % 60)}s` : `${Math.round(dur)}s`
    const label = s.label.replace(/\s+/g, '')
    return `${label}(${timeStr})`
  }).join(' → ')
}

function normalizeBehaviorId(id) {
  const map = {
    implementing: 'implementing',
    thinking: 'thinking',
    debugging: 'debugging',
    seekingHelp: 'seeking_help',
    seeking_help: 'seeking_help',
    idle: 'thinking',
    testing: 'testing',
  }
  return map[id] || 'thinking'
}

function computeBigrams(segments) {
  const bigrams = {}
  for (let i = 0; i < segments.length - 1; i++) {
    const from = normalizeBehaviorId(segments[i].id)
    const to = normalizeBehaviorId(segments[i + 1].id)
    const key = `${from}_${to}`
    bigrams[key] = (bigrams[key] || 0) + 1
  }
  return bigrams
}

// ─── Dominant Strategy ───────────────────────────────────────

function classifyStrategy(bigrams, segCounts) {
  const seekCount = (segCounts.seeking_help || 0)
  const debugCount = (segCounts.debugging || 0)
  const implCount = (segCounts.implementing || 0)
  const thinkSeek = (bigrams.thinking_seeking_help || 0) + (bigrams.implementing_seeking_help || 0)
  const implDebug = (bigrams.implementing_debugging || 0) + (bigrams.debugging_implementing || 0)
  const debugDebug = (bigrams.debugging_debugging || 0)

  if (thinkSeek > implDebug && thinkSeek > debugDebug) return 'Help-Seeking Loop'
  if (debugDebug >= 3 || (debugCount > implCount && debugCount > seekCount)) return 'Debug Trap'
  return 'Coding Cycle'
}

// ─── Per-Query Profile Computation ───────────────────────────

function computePerQueryProfiles(events, dbMessages) {
  const queries = events.filter(e => e.type === 'CHAT_QUERY').sort((a, b) => a.timestamp - b.timestamp)
  const aiResponses = dbMessages.filter(m => m.sender_id === AI_AGENT_ID)
  const profiles = []

  for (let i = 0; i < queries.length; i++) {
    const qTs = queries[i].timestamp
    const prevAi = aiResponses.filter(m => new Date(m.created_time).getTime() < qTs)
    const windowStart = prevAi.length > 0
      ? new Date(prevAi[prevAi.length - 1].created_time).getTime()
      : (events.length > 0 ? events[0].timestamp : 0)

    const windowEvents = events.filter(e => e.timestamp >= windowStart && e.timestamp < qTs)
    const edits = windowEvents.filter(e => e.type === 'CODE_TYPE' || e.type === 'CODE_DELETE').length
    const runs = windowEvents.filter(e => e.type === 'TERMINAL_RUN').length
    const errors = windowEvents.filter(e => e.type === 'TERMINAL_ERROR').length

    profiles.push(classifyProfile(edits, runs, errors, prevAi.length > 0))
  }
  return profiles
}

function dominantProfile(profiles) {
  if (profiles.length === 0) return ''
  const counts = {}
  profiles.forEach(p => { counts[p] = (counts[p] || 0) + 1 })
  return Object.entries(counts).sort(([, a], [, b]) => b - a)[0][0]
}

// ─── Main Builder ────────────────────────────────────────────

export async function buildSessionDataset(students, eventsByUser, sessionId, analyticsById, deployment = '') {
  const rows = []

  for (const student of students) {
    const events = eventsByUser[student.id] || eventsByUser[String(student.id)] || []
    if (events.length === 0) continue

    const analytics = analyticsById?.[student.id]?.analytics || analyticsById?.[String(student.id)]?.analytics || null
    const name = student.display_name || student.username || `${student.first_name || ''} ${student.last_name || ''}`.trim() || 'Unknown'

    // ── DB messages for profile computation ──
    let dbMessages = []
    try {
      dbMessages = await getMessagesBetweenUserAndAI(student.id, sessionId)
    } catch (err) {
      console.error(`Failed to fetch messages for ${name}:`, err)
    }

    // ── Demographics ──
    const demo = student.demographic || {}

    // ── Segments & Sequence ──
    const segments = parseSegments(analytics)
    const fullSequence = buildSequenceString(segments)

    const segCounts = { thinking: 0, implementing: 0, debugging: 0, seeking_help: 0, testing: 0 }
    const segTimes = { thinking: 0, implementing: 0, debugging: 0, seeking_help: 0, testing: 0 }
    segments.forEach(s => {
      const norm = normalizeBehaviorId(s.id)
      segCounts[norm] = (segCounts[norm] || 0) + 1
      segTimes[norm] = (segTimes[norm] || 0) + s.duration
    })

    // ── Bigrams ──
    const bigrams = computeBigrams(segments)

    // ── Dominant Bigram ──
    const bigramEntries = Object.entries(bigrams).sort(([, a], [, b]) => b - a)
    const dominantBigram = bigramEntries.length > 0 ? bigramEntries[0][0] : ''
    const dominantBigramCount = bigramEntries.length > 0 ? bigramEntries[0][1] : 0

    // ── Session-Level Interaction ──
    const queryEvents = events.filter(e => e.type === 'CHAT_QUERY')
    const totalQueries = queryEvents.length
    const totalRuns = events.filter(e => e.type === 'TERMINAL_RUN').length
    const totalErrors = events.filter(e => e.type === 'TERMINAL_ERROR').length
    const totalEdits = events.filter(e => e.type === 'CODE_TYPE' || e.type === 'CODE_INDENT').length
    const totalPastes = events.filter(e => e.type === 'CODE_PASTE').length

    const sessionStart = events[0].timestamp
    const sessionEnd = events[events.length - 1].timestamp
    const sessionDuration = Math.round((sessionEnd - sessionStart) / 1000)

    const firstQuery = queryEvents[0]
    const timeToFirstQuery = firstQuery ? Math.round((firstQuery.timestamp - sessionStart) / 1000) : ''

    // Code at first query
    const firstQueryMsg = dbMessages.find(m => m.sender_id !== AI_AGENT_ID)
    const codeAtFirstQuery = firstQueryMsg?.code_context?.length || 0

    // ── Outcome ──
    const testEvents = events.filter(e => e.type === 'TEST_CASE_RESULT')
    const lastTest = testEvents[testEvents.length - 1]
    const finalPassed = lastTest?.payload?.passed_count ?? ''
    const finalTotal = lastTest?.payload?.total_tests ?? ''
    const taskCompleted = (finalTotal > 0 && finalPassed === finalTotal) ? 1 : 0

    // ── Survey ──
    const post = student.postSurvey || {}

    // ── Derived ──
    const profiles = computePerQueryProfiles(events, dbMessages)
    const domProfile = dominantProfile(profiles)
    const domStrategy = classifyStrategy(bigrams, segCounts)

    // ── All Bigram Keys ──
    const bigramKeys = [
      'thinking_thinking', 'thinking_implementing', 'thinking_debugging', 'thinking_seeking_help', 'thinking_testing',
      'implementing_thinking', 'implementing_implementing', 'implementing_debugging', 'implementing_seeking_help', 'implementing_testing',
      'debugging_thinking', 'debugging_implementing', 'debugging_debugging', 'debugging_seeking_help', 'debugging_testing',
      'seeking_help_thinking', 'seeking_help_implementing', 'seeking_help_debugging', 'seeking_help_seeking_help', 'seeking_help_testing',
      'testing_thinking', 'testing_implementing', 'testing_debugging', 'testing_seeking_help', 'testing_testing',
    ]

    rows.push({
      student_id: student.id,
      student_name: name,
      session_id: sessionId,
      deployment,
      // Demographics
      demo_gender: demo.gender || '',
      demo_year: demo.year || '',
      demo_major: demo.major || '',
      demo_python_experience: demo.python_experience || '',
      demo_llm_usage: demo.llm_usage || '',
      // Sequence
      full_behavior_sequence: fullSequence,
      total_segments: segments.length,
      segment_count_thinking: segCounts.thinking,
      segment_count_implementing: segCounts.implementing,
      segment_count_debugging: segCounts.debugging,
      segment_count_seeking_help: segCounts.seeking_help,
      segment_count_testing: segCounts.testing,
      // Time
      total_thinking_s: Math.round(segTimes.thinking),
      total_implementing_s: Math.round(segTimes.implementing),
      total_debugging_s: Math.round(segTimes.debugging),
      total_seeking_help_s: Math.round(segTimes.seeking_help),
      total_testing_s: Math.round(segTimes.testing),
      session_duration_s: sessionDuration,
      // Bigrams
      ...Object.fromEntries(bigramKeys.map(k => [`bigram_${k}`, bigrams[k] || 0])),
      // Dominant bigram
      dominant_bigram: dominantBigram,
      dominant_bigram_count: dominantBigramCount,
      // Interaction
      total_queries: totalQueries,
      total_terminal_runs: totalRuns,
      total_terminal_errors: totalErrors,
      total_code_edits: totalEdits,
      total_code_pastes: totalPastes,
      time_to_first_query_s: timeToFirstQuery,
      code_at_first_query: codeAtFirstQuery,
      // Outcome
      task_completed: taskCompleted,
      final_test_passed: finalPassed,
      final_test_total: finalTotal,
      // Survey
      post_task_difficulty: post.task_difficulty || '',
      post_helpfulness: post.helpfulness || '',
      post_understanding_improvement: post.understanding_improvement || '',
      post_ui_intuitiveness: post.ui_intuitiveness || '',
      // Derived
      dominant_strategy: domStrategy,
      dominant_profile: domProfile,
      experiment_group: student.experiment_group || '',
    })
  }

  return rows
}

// ─── CSV Export ───────────────────────────────────────────────

export function downloadSessionCSV(rows, filename) {
  if (rows.length === 0) return
  const headers = Object.keys(rows[0])
  const csv = [
    headers.join(','),
    ...rows.map(row => headers.map(h => {
      const v = row[h]
      if (typeof v === 'string' && (v.includes(',') || v.includes('"') || v.includes('\n'))) return esc(v)
      return v ?? ''
    }).join(','))
  ].join('\n')

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url; a.download = filename; a.click()
  URL.revokeObjectURL(url)
}